import { useCallback, useState, useEffect, ReactElement } from "react";
import { ValidatedFormFieldConfig } from "@evergiving/common/dist/es2015/form";

import { RootState, VersionedStringFormState } from "@/lib/redux/types";
import { setFormValue, resetFormErrors } from "@/lib/redux/actions";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";
import { InputFieldConfig } from "@/lib/misc/InputFieldConfig";
import { FieldError } from "@/lib/components/FieldError";
import { useAppDispatch, useAppSelector } from "@/lib/hooks/redux";
import { useShowErrors } from "@/lib/hooks/useShowErrors";
import { normalizeFieldValue } from "@/lib/misc/validations";
import { useAutofill } from "@/lib/hooks/useAutofill";

export function FormField({
  config,
  name,
  label,
  htmlName,
  autoComplete,
  autoCapitalize,
  autoFocus,
  inputMode,
  spellCheck,
}: {
  config: ValidatedFormFieldConfig;
  name: keyof VersionedStringFormState;
  label?: boolean;
} & InputFieldConfig): ReactElement {
  const required = !!config.requiredFieldErrorText;
  const valueFromStore = useAppSelector(
    (state: RootState) => state[name].value
  );
  const [currentValue, setCurrentValue] = useState<string>(
    valueFromStore || ""
  );
  const anyErrors = useShowErrors(config, name, currentValue);
  const dispatch = useAppDispatch();
  const fieldValidationErrors = useFieldValidationErrors(config);
  const [isDirty, setIsDirty] = useState(false);

  const saveData = useCallback(
    (value: string) => {
      const newValue = normalizeFieldValue(value);

      dispatch(
        setFormValue(
          name,
          newValue,
          fieldValidationErrors.forValue(newValue).length > 0
        )
      );

      setIsDirty(true);
    },
    [dispatch, fieldValidationErrors, name]
  );

  const {
    detectAutofillByAnimation,
    handleBlurWithAutofill,
    handleFocusWithAutofill,
    handleInputWithAutofillSafariHack,
    saveDataWithAutofill,
  } = useAutofill({ saveData });

  const handleChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      saveDataWithAutofill(value);

      setCurrentValue(value);
      dispatch(resetFormErrors(name));
    },
    [dispatch, name, saveDataWithAutofill, setCurrentValue]
  );

  useEffect(() => {
    setCurrentValue(valueFromStore || "");
  }, [valueFromStore, setCurrentValue]);

  const errorId = `${name}-errors`;

  const inputField = (
    <>
      {/* eslint-disable jsx-a11y/no-autofocus */}
      {/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ Discussion of this override follows:

       We're only passing through the attribute here.  See DonationDetailsPage for
       a discussion why we would ever want to set this attribute.

       */}
      <input
        className="textField"
        type="text"
        name={htmlName}
        placeholder={config.placeholderText}
        value={currentValue}
        onAnimationStart={detectAutofillByAnimation}
        onBlur={handleBlurWithAutofill}
        onChange={handleChange}
        onFocus={handleFocusWithAutofill}
        onInput={handleInputWithAutofillSafariHack}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        autoFocus={autoFocus}
        inputMode={inputMode}
        spellCheck={spellCheck}
        aria-required={required}
        aria-invalid={(anyErrors && isDirty) || undefined}
        aria-describedby={anyErrors ? errorId : undefined}
      />
      {/* eslint-enable jsx-a11y/no-autofocus */}
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={fieldValidationErrors.forValue(
              normalizeFieldValue(currentValue)
            )}
          />
        ) : null}
      </div>
    </>
  );

  if (label === false) {
    return inputField;
  }

  return (
    <LabelledField id={name}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>
      {inputField}
    </LabelledField>
  );
}
